import { useEffect } from 'react';
import { ArrowPathIcon, HomeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

interface AppErrorPageProps {
  homeHref?: string;
  onRefresh?: () => void;
}

const describeRouteError = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
};

export const AppErrorPage = ({
  homeHref = '/',
  onRefresh = () => window.location.reload(),
}: AppErrorPageProps) => (
  <div className="min-h-screen overflow-y-auto bg-gray-950 px-4 py-8 text-gray-100 sm:px-6 lg:px-8">
    <main className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-4xl flex-col justify-center">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-800 ring-1 ring-gray-700">
          <span className="text-lg font-semibold text-white">α</span>
        </div>
        <span className="text-base font-semibold tracking-tight text-white">Alpha</span>
      </div>

      <section className="overflow-hidden rounded-lg border border-gray-800 bg-gray-900 shadow-2xl shadow-black/30">
        <div className="border-b border-gray-800 bg-gray-950/70 px-4 py-3 sm:px-6">
          <span className="inline-flex items-center gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-200">
            <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" />
            Page unavailable
          </span>
        </div>

        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_15rem] lg:items-center">
          <div>
            <p className="text-sm font-medium uppercase text-gray-500">Refresh recommended</p>
            <h1 className="mt-3 max-w-2xl text-3xl font-semibold leading-tight text-white sm:text-4xl">
              We couldn't get this page ready.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-gray-300">
              Refresh the app to continue. If this keeps happening, return home and try the
              action again in a moment.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onRefresh}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-600 bg-gray-100 px-4 text-sm font-semibold text-gray-950 transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                Refresh
              </button>
              <a
                href={homeHref}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-gray-700 bg-gray-950 px-4 text-sm font-semibold text-gray-100 transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                <HomeIcon className="h-4 w-4" aria-hidden="true" />
                Home
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-gray-800 bg-gray-950 p-4">
            <div className="flex items-center justify-between border-b border-gray-800 pb-3">
              <span className="text-xs font-medium uppercase text-gray-500">Status</span>
              <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
            </div>
            <div className="mt-4 space-y-3 text-sm text-gray-300">
              <div className="flex items-center justify-between gap-4">
                <span>Session</span>
                <span className="font-medium text-gray-100">Kept</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Navigation</span>
                <span className="font-medium text-amber-200">Paused</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span>Next step</span>
                <span className="font-medium text-gray-100">Refresh</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export const RouteErrorPage = () => {
  const error = useRouteError();

  useEffect(() => {
    console.error('Route rendering failed:', describeRouteError(error), error);
  }, [error]);

  return <AppErrorPage />;
};
